/** @format */

import { buildWidgetClickParams } from 'src/analytics/buildGa4EventPayload';
import { trackGa4 } from 'src/analytics/trackGa4';
import type { Ga4WidgetClickParams } from 'src/analytics/ga4Parameters';
import type { TrackGa4Options } from 'src/analytics/trackGa4';

/** Maps CMS widget `type` to analytics `widget_name` when they differ. */
const WIDGET_TYPE_TO_GA4_NAME: Readonly<Record<string, string>> = Object.freeze({
  news: 'latest_news',
  event: 'latest_events',
  events: 'latest_events',
  knowledge_base: 'knowledge_base',
  academy: 'academy',
  academy_training: 'academy_training',
  extranet: 'extranet',
  product_data: 'extranet_product_data',
  multi_link: 'multi_link',
  information: 'information',
  hero: 'hero'
});

const TOP_ROW_MAX_Y = 3;
const MIDDLE_ROW_MAX_Y = 8;

export interface WidgetGa4Data {
  widget_name?: unknown;
  analytics_widget_name?: unknown;
  widget_category?: unknown;
  widget_position?: unknown;
  type?: unknown;
  y?: unknown;
  [key: string]: unknown;
}

export function resolveWidgetGa4Name(data: WidgetGa4Data = {}): string {
  if (typeof data.widget_name === 'string' && data.widget_name.trim()) {
    return data.widget_name.trim();
  }

  if (typeof data.analytics_widget_name === 'string' && data.analytics_widget_name.trim()) {
    return data.analytics_widget_name.trim();
  }

  const type = data.type;
  if (type && typeof type === 'string' && WIDGET_TYPE_TO_GA4_NAME[type]) {
    return WIDGET_TYPE_TO_GA4_NAME[type];
  }

  if (!type) {
    return 'unknown_widget';
  }

  return String(type)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toLowerCase();
}

export function resolveWidgetGa4Category(data: WidgetGa4Data = {}): string | undefined {
  if (typeof data.widget_category === 'string' && data.widget_category.trim()) {
    return data.widget_category.trim();
  }

  return data.type ? String(data.type) : undefined;
}

/**
 * Derives vertical placement from react-grid-layout `y` (0 = top).
 */
export function resolveWidgetGa4Position(data: WidgetGa4Data = {}): 'top' | 'middle' | 'bottom' | string {
  if (typeof data.widget_position === 'string' && data.widget_position.trim()) {
    return data.widget_position.trim();
  }

  const y = Number(data.y);
  if (!Number.isFinite(y)) {
    return 'top';
  }

  if (y <= TOP_ROW_MAX_Y) {
    return 'top';
  }

  if (y <= MIDDLE_ROW_MAX_Y) {
    return 'middle';
  }

  return 'bottom';
}

export function trackWidgetClick(params: Ga4WidgetClickParams = {}, options: TrackGa4Options = {}): void {
  trackGa4('widget_click', buildWidgetClickParams(params), options);
}
